import { useEffect, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { FormProvider, useForm } from "react-hook-form";
import { PaziaFormButton, PaziaInput, PaziaFileDropzone, PaziaSelect } from "components/atoms";
import { PaziaModal } from "components/molecules";
import { ModalHeader } from "components/templates/Modals/ModalHeader";
import { ModalPadding } from "components/templates/Modals/ModalPadding";
import { AuthorApi, authorsApi } from "api/shop";
import { useShopImageUpload } from "pages/Shop/Sections/SectionForms/useShopImageUpload";
import { useTrainersOptions } from "api/hooks/useTrainers";
import { useToaster } from "utils";

const FORM_NAMES = {
  FIRST_NAME: "firstName",
  LAST_NAME: "lastName",
  AVATAR: "avatar",
} as const;

const schema = yup
  .object({
    [FORM_NAMES.FIRST_NAME]: yup.string().required("Imię jest wymagane"),
    [FORM_NAMES.LAST_NAME]: yup.string().required("Nazwisko jest wymagane"),
    [FORM_NAMES.AVATAR]: yup.string().optional(),
  })
  .required();

interface FormValues {
  firstName: string;
  lastName: string;
  avatar?: string;
}

interface AuthorFormModalProps {
  isOpen: boolean;
  onCloseRequest: () => void;
  author?: AuthorApi;
  onSuccess: (author: AuthorApi) => void;
}

export const AuthorFormModal = ({ isOpen, onCloseRequest, author, onSuccess }: AuthorFormModalProps) => {
  const { uploadImage } = useShopImageUpload();
  const { showSuccess, showError } = useToaster();
  const { isLoading: trainersLoading, options: trainerOptions } = useTrainersOptions();
  const [isSaving, setIsSaving] = useState(false);

  const methods = useForm<FormValues>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      [FORM_NAMES.FIRST_NAME]: author?.firstName ?? "",
      [FORM_NAMES.LAST_NAME]: author?.lastName ?? "",
      [FORM_NAMES.AVATAR]: author?.avatar ?? "",
    },
  });
  const { handleSubmit, reset, setValue } = methods;

  useEffect(() => {
    if (isOpen) {
      reset({
        [FORM_NAMES.FIRST_NAME]: author?.firstName ?? "",
        [FORM_NAMES.LAST_NAME]: author?.lastName ?? "",
        [FORM_NAMES.AVATAR]: author?.avatar ?? "",
      });
    }
  }, [isOpen, author, reset]);

  const handleImportFromTrainer = (trainerId: string) => {
    const trainer = trainerOptions.find((o) => o.value === trainerId)?.trainer;
    if (!trainer) return;
    setValue(FORM_NAMES.FIRST_NAME, trainer.firstName, { shouldDirty: true });
    setValue(FORM_NAMES.LAST_NAME, trainer.surname, { shouldDirty: true });
    setValue(FORM_NAMES.AVATAR, trainer.avatar, { shouldDirty: true });
  };

  const handleFormSubmit = async (data: FormValues) => {
    setIsSaving(true);
    try {
      if (author) {
        await authorsApi.updateAuthor(author.id, data);
        showSuccess("Zaktualizowano autora");
        onSuccess({ id: author.id, ...data });
      } else {
        const res = await authorsApi.createAuthor(data);
        showSuccess("Dodano autora");
        onSuccess({ id: res.data.id, ...data });
      }
    } catch (e) {
      showError("Nie udało się zapisać autora");
      console.error(e);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <PaziaModal id="author-form-modal" isOpen={isOpen} onCloseRequest={onCloseRequest}>
      {isOpen && (
        <ModalPadding>
          <ModalHeader>{author ? "Edytuj autora" : "Nowy autor"}</ModalHeader>
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(handleFormSubmit)} className="grid gap-4">
              {!author && (
                <PaziaSelect
                  name="importFromTrainer"
                  label="Importuj z trenera"
                  options={trainerOptions}
                  isLoading={trainersLoading}
                  onChange={(opt) => opt && handleImportFromTrainer(opt.value)}
                  hideErrors
                />
              )}
              <PaziaInput name={FORM_NAMES.FIRST_NAME} label="Imię" required />
              <PaziaInput name={FORM_NAMES.LAST_NAME} label="Nazwisko" required />
              <div className="[&_img]:max-h-32">
                <PaziaFileDropzone
                  name={FORM_NAMES.AVATAR}
                  label="Avatar"
                  type="image"
                  single
                  preview="image"
                  onDrop={async (file: File) => {
                    const url = await uploadImage(file as File);
                    setValue(FORM_NAMES.AVATAR, url);
                  }}
                />
              </div>
              <PaziaFormButton disabled={isSaving} />
            </form>
          </FormProvider>
        </ModalPadding>
      )}
    </PaziaModal>
  );
};
